type CredentialMethod = "create" | "get";

type CredentialPatchState = {
  activeCeremonies: number;
  createDescriptor: PropertyDescriptor | undefined;
  getDescriptor: PropertyDescriptor | undefined;
  originalCreate: CredentialsContainer["create"];
  originalGet: CredentialsContainer["get"];
};

const activePatches = new WeakMap<CredentialsContainer, CredentialPatchState>();

function restoreMethod(
  credentials: CredentialsContainer,
  method: CredentialMethod,
  descriptor: PropertyDescriptor | undefined,
): void {
  if (descriptor) {
    Object.defineProperty(credentials, method, descriptor);
    return;
  }

  // The browser methods normally live on CredentialsContainer.prototype. If
  // there was no own property before the patch, deleting ours restores that
  // exact inherited state instead of leaving a bound function behind.
  if (!Reflect.deleteProperty(credentials, method)) {
    throw new TypeError(`Unable to restore navigator.credentials.${method}`);
  }
}

function requiredCreationOptions(
  options?: CredentialCreationOptions,
): CredentialCreationOptions | undefined {
  if (!options?.publicKey) return options;

  return {
    ...options,
    publicKey: {
      ...options.publicKey,
      authenticatorSelection: {
        ...options.publicKey.authenticatorSelection,
        userVerification: "required",
      },
    },
  };
}

function requiredRequestOptions(
  options?: CredentialRequestOptions,
): CredentialRequestOptions | undefined {
  if (!options?.publicKey) return options;

  return {
    ...options,
    publicKey: {
      ...options.publicKey,
      userVerification: "required",
    },
  };
}

function installPatch(credentials: CredentialsContainer): CredentialPatchState {
  const existing = activePatches.get(credentials);
  if (existing) return existing;

  const state: CredentialPatchState = {
    activeCeremonies: 0,
    createDescriptor: Object.getOwnPropertyDescriptor(credentials, "create"),
    getDescriptor: Object.getOwnPropertyDescriptor(credentials, "get"),
    originalCreate: credentials.create.bind(credentials),
    originalGet: credentials.get.bind(credentials),
  };

  Object.defineProperty(credentials, "create", {
    value: (options?: CredentialCreationOptions) =>
      state.originalCreate(requiredCreationOptions(options)),
    configurable: true,
    writable: true,
  });

  try {
    Object.defineProperty(credentials, "get", {
      value: (options?: CredentialRequestOptions) =>
        state.originalGet(requiredRequestOptions(options)),
      configurable: true,
      writable: true,
    });
  } catch (error) {
    restoreMethod(credentials, "create", state.createDescriptor);
    throw error;
  }

  activePatches.set(credentials, state);
  return state;
}

function releasePatch(
  credentials: CredentialsContainer,
  state: CredentialPatchState,
): void {
  state.activeCeremonies -= 1;
  if (state.activeCeremonies > 0) return;

  // Delete the state first so a restoration failure cannot leave a stale
  // refcount that makes later ceremonies trust a partially restored patch.
  activePatches.delete(credentials);
  try {
    restoreMethod(credentials, "create", state.createDescriptor);
  } finally {
    restoreMethod(credentials, "get", state.getDescriptor);
  }
}

/**
 * Enforce local user verification while the Stytch SDK performs a WebAuthn
 * ceremony. The installation is shared and reference-counted because multiple
 * React mutations can overlap on the process-global CredentialsContainer.
 */
export function runWithRequiredUserVerification<T>(
  credentials: CredentialsContainer,
  ceremony: () => Promise<T>,
): Promise<T> {
  const state = installPatch(credentials);
  state.activeCeremonies += 1;

  let result: Promise<T>;
  try {
    result = ceremony();
  } catch (error) {
    releasePatch(credentials, state);
    throw error;
  }

  return result.finally(() => releasePatch(credentials, state));
}
